import {
    Injectable, inject
} from "@angular/core";
import {
    ModelAction, ISearchSort, SearchSortDirection
} from "@shopliftr/common-js/data";
import {
    BulkResponse, BulkUtil, CreateRequest, RequestUtil,
    SearchResponse, UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    AdTag, AdTagSearchFilterOptions
} from "@shopliftr/common-js/smart";
import {
    ICreateInternalApiClientService, IUpdateInternalApiClientService, InternalApiRequestService, ListUtil,
    IQueryInternalApiClientService
} from "@shopliftr/common-ng";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * API client service for managing ad tags.
 *
 * Implements the API Client Service pattern by providing a focused interface
 * for ad tag CRUD operations against the Internal API. This service is provided
 * at the root level and should be injected into list data services or components
 * that need to interact with ad tag endpoints.
 *
 * **Service Pattern:** API Client Service
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @implements {ICreateInternalApiClientService<AdTag>} Standard internal API create client pattern
 * @implements {IUpdateInternalApiClientService<AdTag>} Standard internal API update client pattern
 * @implements {IQueryInternalApiClientService<AdTag>} Standard internal API query client pattern
 *
 * @example
 * ```typescript
 * // Inject into a component or service
 * private readonly _adTagApiClientService = inject(AdTagApiClientService);
 *
 * // Fetch all ad tags with filtering and sorting
 * const filterOptions = new AdTagSearchFilterOptions();
 * this._adTagApiClientService.getAll$(
 *     filterOptions,
 *     [{ field: 'name', direction: SearchSortDirection.Ascending }]
 * ).subscribe(tags => console.log(tags));
 * ```
 */
@Injectable({ providedIn: "root" })
export class AdTagApiClientService implements ICreateInternalApiClientService<AdTag>,
 IUpdateInternalApiClientService<AdTag>, IQueryInternalApiClientService<AdTag> {
    /**
     * Base API path for ad tag endpoints.
     */
    readonly basePath = "ad-tag";

    /**
     * Internal API request service for making HTTP requests
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves multiple ad tags by their unique identifiers in a single bulk request.
     *
     * @param ids Array of unique identifiers of the ad tags to retrieve
     * @returns Observable that emits a BulkResponse containing the requested ad tags
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<AdTag>(id, AdTag.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves a single ad tag by its unique identifier.
     *
     * @param id The unique identifier of the ad tag to retrieve
     * @returns Observable that emits the ad tag data
     */
    get$(id: string): Observable<AdTag> {
        const request = RequestUtil.createGetRequest<void>(id, AdTag.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$<AdTag>(request, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Retrieves all ad tags matching the specified filter options.
     *
     * @param filterOptions - Search filter criteria for ad tags
     * @param sort - Optional array of sort specifications. Defaults to ascending sort by name
     * @returns Observable emitting an array of ad tags matching the filter criteria
     *
     * @example
     * ```typescript
     * // Get all ad tags sorted by name
     * const filterOptions = new AdTagSearchFilterOptions();
     * this._adTagApiClientService.getAll$(filterOptions).subscribe(tags => {
     *     console.log('Ad tags:', tags);
     * });
     *
     * // Get tags with custom sorting
     * const filterOptions = new AdTagSearchFilterOptions();
     * this._adTagApiClientService.getAll$(
     *     filterOptions,
     *     [{ field: 'createdDate', direction: SearchSortDirection.Descending }]
     * ).subscribe(tags => {
     *     console.log('Recently created tags:', tags);
     * });
     * ```
     */
    getAll$(filterOptions: AdTagSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<AdTag>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, AdTag.modelName, undefined, undefined, filterOptions, sort ?? [{
                field: "name",
                direction: SearchSortDirection.Ascending
            }]
        );

        return this._internalApiRequestService.getAll$<AdTag, AdTagSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Searches for ad tags using the provided filter criteria.
     *
     * @param filterOptions The search criteria to apply
     * @param page Zero-based page index for pagination (optional)
     * @param pageSize Number of items per page (optional)
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable that emits search results with pagination metadata
     */
    search$(
        filterOptions: AdTagSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<AdTag>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            AdTag.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Creates a new ad tag in the system.
     *
     * @param fields The ad tag data to create
     * @returns Observable that emits the created ad tag with assigned ID and metadata
     */
    create$(fields: AdTag): Observable<AdTag> {
        const createRequest = RequestUtil.createCreateRequest<AdTag, void>(
            ModelAction.ClientCreate, fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Creates multiple ad tags in a single bulk request.
     *
     * @param items Array of create requests for ad tags to create
     * @returns Observable that emits a BulkResponse with the results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<AdTag, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates an existing ad tag with new field values or removes specified fields.
     *
     * @param id The unique identifier of the ad tag to update
     * @param version The current version of the ad tag for optimistic locking
     * @param fields The ad tag fields to update with new values
     * @param removeFields Array of field names to remove from the ad tag (optional)
     * @returns Observable that emits the updated ad tag
     */
    update$(id: string, version: number, fields: AdTag, removeFields?: Array<string>): Observable<AdTag> {
        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<AdTag> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Updates the record of the original ad tag with the updated model provided.
     *
     * @param original The original ad tag model
     * @param updated The updated ad tag model with changes applied
     * @returns Observable that emits the updated ad tag
     */
    updateWithChanges$(original: AdTag, updated: AdTag): Observable<AdTag> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Updates multiple ad tags in a single bulk request.
     *
     * @param requests Array of update requests for ad tags to update
     * @returns Observable that emits a BulkResponse with the results of each update operation
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
